import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../design-system/button/button';
import { DataTableNew } from '../../../design-system/data-table-new';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { BeneficiaryView } from '../../../types/entity-types';
import { useDeleteBeneficiary } from '../hooks/use-delete-beneficiary';
import { beneficiaryTableColumnsDefinition } from './beneficiary-table-column-definition';
import { DeleteBeneficiaryModal } from './components/delete-beneficiary-modal';

type AllBeneficiariesTableProps = {
  beneficiaries: BeneficiaryView[];
  onGoBack: () => void;
  onInitiateBeneficiaryPayment: (beneficiaryId: string) => void;
};

export const AllBeneficiariesTable = ({
  beneficiaries,
  onGoBack,
  onInitiateBeneficiaryPayment,
}: AllBeneficiariesTableProps) => {
  const [deleteModalState, setDeleteModalState] = useState<{
    isOpen: boolean;
    beneficiaryId: string | null;
    beneficiaryName: string;
    lastPaidDate?: Date;
  }>({
    isOpen: false,
    beneficiaryId: null,
    beneficiaryName: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const { deleteBeneficiary } = useDeleteBeneficiary();

  const handleDeleteBeneficiary = (beneficiaryId: string, beneficiaryName: string, lastPaidDate?: Date) => {
    setDeleteModalState({
      isOpen: true,
      beneficiaryId,
      beneficiaryName,
      lastPaidDate,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalState.beneficiaryId) return;

    setIsDeleting(true);
    try {
      await deleteBeneficiary(deleteModalState.beneficiaryId);
      setDeleteModalState({ isOpen: false, beneficiaryId: null, beneficiaryName: '' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseModal = () => {
    if (!isDeleting) {
      setDeleteModalState({ isOpen: false, beneficiaryId: null, beneficiaryName: '' });
    }
  };

  const table = useReactTable({
    data: beneficiaries,
    columns: beneficiaryTableColumnsDefinition,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      onInitiateBeneficiaryPayment,
      onDeleteBeneficiary: handleDeleteBeneficiary,
    },
    initialState: {
      sorting: [{ id: 'name', desc: false }],
      pagination: {
        pageSize: 20,
      },
    },
  });

  return (
    <>
      <Stack align="stretch" space={4} className="bg-white px-6 pt-4">
        <Inline justify="between">
          <Inline space={3} wrap="nowrap">
            <Button variant="ghost" size="icon" className="bg-muted" onClick={onGoBack} aria-label="Go back">
              <div className="h-9 w-9 rounded-full bg-muted p-1">
                <ChevronLeft className="h-full w-full" />
              </div>
            </Button>

            <h1 className="text-2xl font-semibold text-nowrap text-black">All recipients</h1>
          </Inline>

          <DataTableNew.GlobalSearch table={table} placeholder="Search recipients" />
        </Inline>

        <DataTableNew.Table table={table} noResultsMessage="No recipients found" />

        {table.getPageCount() > 1 && <DataTableNew.Pagination table={table} />}
      </Stack>

      <DeleteBeneficiaryModal
        isOpen={deleteModalState.isOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        beneficiaryName={deleteModalState.beneficiaryName}
        lastPaidDate={deleteModalState.lastPaidDate}
        isLoading={isDeleting}
      />
    </>
  );
};
